"use client";

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { FilterablePaginatedSectionContext } from '../../SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext';
interface FlagsState {
  [key: number]: boolean;
  isTopAnchorVisible: boolean;
  isTransitioning: boolean;
}

type FlagsAction = 
  | { type: 'TOGGLE_FLAG'; index: number }
  | { type: 'SET_FLAG'; index: number; value: boolean }
  | { type: 'SET_TOP_ANCHOR_VISIBLE'; value: boolean }
  | { type: 'SET_TRANSITIONING'; value: boolean };

interface FlagsContextValue {
  flags: FlagsState;
  setFlag: (index: number, value: boolean) => void;
  setTopAnchorVisible: (value: boolean) => void;
  setTransitioning: (value: boolean) => void;
}

const SDUIFlowLayoutItemOnVisibilityRendererContext = createContext<FlagsContextValue | undefined>(undefined);

const flagsReducer = (state: FlagsState, action: FlagsAction): FlagsState => {
  switch (action.type) {
    case 'SET_FLAG':
      return {
        ...state,
        [action.index]: action.value
      };
    case 'SET_TOP_ANCHOR_VISIBLE':
      return {
        ...state,
        isTopAnchorVisible: action.value
      };
    case 'SET_TRANSITIONING':
      return {
        ...state,
        isTransitioning: action.value
      };
    default:
      return state;
  }
};

interface Props {
  children: ReactNode;
  initialCount?: number;
}

export const SDUIFlowLayoutItemOnVisibilityRendererProvider: React.FC<Props> = ({ 
  children,
  initialCount = 0 
}) => {
  const { setIsTransitioningFunction } = useContext(FilterablePaginatedSectionContext);
  const initialState: FlagsState = Array(initialCount)
    .fill(false)
    .reduce((acc, _, index) => ({ ...acc, [index]: false }), {});

  const [flags, dispatch] = useReducer(flagsReducer, initialState);

  const setFlag = useCallback((index: number, value: boolean) => {
    dispatch({ type: 'SET_FLAG', index, value });
  }, []);

  const setTopAnchorVisible = useCallback((value: boolean) => {
    dispatch({ type: 'SET_TOP_ANCHOR_VISIBLE', value });
  }, []);

  const setTransitioning = useCallback((value: boolean) => {
    dispatch({ type: 'SET_TRANSITIONING', value });
  }, []);

  useEffect(() => {
    setIsTransitioningFunction?.(() =>setTransitioning);
  }, [setIsTransitioningFunction, setTransitioning]);

  const value = {
    flags,
    setFlag,
    setTopAnchorVisible,
    setTransitioning,
  };

  return (
    <SDUIFlowLayoutItemOnVisibilityRendererContext.Provider value={value}>
      {flags.isTransitioning ? (
        // Large enough height to ensure that the transition is visible
        <div className="w-full h-[3500px]" />
      ) : (
        children
      )}
    </SDUIFlowLayoutItemOnVisibilityRendererContext.Provider>
  );
};

export const useSDUIFlowLayoutItemOnVisibilityRendererFlags = (): FlagsState | undefined => {
  const context = useContext(SDUIFlowLayoutItemOnVisibilityRendererContext);
  return context?.flags;
};

export const useSDUIFlowLayoutItemOnVisibilityRendererSetFlag = (): ((index: number, value: boolean) => void) | undefined => {
  const context = useContext(SDUIFlowLayoutItemOnVisibilityRendererContext);
  return context?.setFlag;
};

export const useSDUIFlowLayoutItemOnVisibilityRendererSetTopAnchorVisible = (): ((value: boolean) => void) | undefined => {
  const context = useContext(SDUIFlowLayoutItemOnVisibilityRendererContext);
  return context?.setTopAnchorVisible;
};
